import React, { ReactNode, useState } from 'react'
import { UnderwritingOffer } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedCurrency, FormattedMessage, useIntl } from '@divvy-web/i18n'
import Badge, { BADGE_COLOR_ACTION } from '@divvy-web/skylab.badge'
import BasicButton, { BASIC_BUTTON_TYPE_FILLED, BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'
import Tile, { TILE_SELECTED_COLOR_ACTION } from '@divvy-web/skylab.tile'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import AgreementModal from '@sputnik/components/AgreementModal'
import AgreementPopsheet from '@sputnik/components/AgreementPopsheet'
import Divider from '@sputnik/components/Divider'
import useDeviceDetect from '@sputnik/hooks/useDeviceDetect'
import { useAcceptTilaAgreement } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/AcceptTilaAgreement.gql'
import { GetCreditApplicationTilaInfo_creditApplication_CreditApplication as CreditApplicationType } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/GetCreditApplicationTilaInfo.gql'
import EligibleOfferTileLearnMoreLink from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/EligibleOfferTileLearnMoreLink/EligibleOfferTileLearnMoreLink'
import { GetEligibleOffers_decisions_Decision } from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/GetEligibleOffers.gql'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

interface EligibleOfferTileProps {
  decision: GetEligibleOffers_decisions_Decision | null | undefined
  bulletPoints: ReactNode[]
  creditApplication: CreditApplicationType
  description: ReactNode
  hasAgreedToTila: boolean
  hasTilaContent: boolean
  subtitle?: ReactNode
  title: ReactNode
  learnMoreLink: string
  setSelectedOffer: React.Dispatch<React.SetStateAction<GetEligibleOffers_decisions_Decision | null | undefined>>
  isSelected: boolean
}

const EligibleOfferTile = ({
  decision,
  bulletPoints,
  creditApplication,
  description,
  hasAgreedToTila,
  hasTilaContent,
  subtitle,
  title,
  learnMoreLink,
  setSelectedOffer,
  isSelected,
}: EligibleOfferTileProps) => {
  const [getClassName, makeTestId] = useNameFormatter('EligibleOfferTile')
  const { formatMessage } = useIntl()
  const { isMobile } = useDeviceDetect()
  const [isShowingAgreementModal, setIsShowingAgreementModal] = useState(false)
  const isMostPopular = decision?.offerType === UnderwritingOffer.FIXED_LINE

  const showToast = useToast()

  const tilaContent = creditApplication?.tila?.content

  const [acceptTilaAgreement, { loading: isAcceptingTilaAgreement }] = useAcceptTilaAgreement({
    onCompleted: () => {
      setIsShowingAgreementModal(false)
      setSelectedOffer(decision)

      logInfo('acceptTilaAgreement', {
        attributes: {
          creditAppId: creditApplication?.salesforceCreditId,
          result: 'accepts agreement terms for application',
        },
        eventName: 'eligibleOfferTile',
      })

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='You have successfully agreed to your offer summary'
          id='sputnik.EligibleOfferTile__VFXi23'
        />,
      )
    },
    onError: (e) => {
      setIsShowingAgreementModal(false)

      logError(e, {
        attributes: {
          creditAppId: creditApplication?.salesforceCreditId,
          action: 'acceptTilaAgreement',
          message: e?.message,
          result: 'Error accepting agreement terms for application',
        },
        eventName: 'eligibleOfferTile',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='There was an error agreeing to your offer summary.'
          id='sputnik.EligibleOfferTile__9fJ3od'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  const handleViewOfferDetailsClick = () => {
    hasTilaContent ? setIsShowingAgreementModal(true) : setSelectedOffer(decision)
  }

  const handleAgreeToOfferSummary = () => {
    if (decision?.offerType && creditApplication) {
      acceptTilaAgreement({
        variables: {
          salesforceCreditId: creditApplication?.salesforceCreditId as string,
          underwritingDecisionUuid: decision?.id,
          underwritingOfferType: decision?.offerType,
        },
        refetchQueries: ['GetTilaInfo'],
      })
    }
  }

  const { approvedAmountInCents: approvedAmount } = decision || {}

  const hasApprovedAmmount = approvedAmount !== undefined && approvedAmount > 0

  return (
    <>
      <Tile
        hidePadding
        className={styles[getClassName('')]}
        dataTestId={makeTestId(decision?.offerType || '')}
        isSelected={isSelected}
        selectedColor={TILE_SELECTED_COLOR_ACTION}
      >
        <div className={styles[getClassName('inner')]}>
          <div className={styles[getClassName('content-wrapper')]}>
            <div className={styles[getClassName('title-wrapper')]}>
              {isMostPopular && (
                <Badge color={BADGE_COLOR_ACTION}>
                  <FormattedMessage
                    defaultMessage='Most popular'
                    id='sputnik.EligibleOfferTile__dC3u7Z'
                  />
                </Badge>
              )}
              {hasApprovedAmmount && (
                <div
                  className={styles[getClassName('amount')]}
                  data-testid={makeTestId('amount')}
                >
                  <FormattedCurrency
                    maximumFractionDigits={0}
                    minimumFractionDigits={2}
                    value={approvedAmount}
                  />
                </div>
              )}
              <div className={styles[getClassName(hasApprovedAmmount ? 'subtitle' : 'title')]}>{title}</div>
              {subtitle && <div className={styles[getClassName('subtitle')]}>{subtitle}</div>}
            </div>
            <div className={styles[getClassName('description')]}>{description}</div>
            <Divider className={styles[getClassName('divider')]} />
            <div>
              <ul className={styles[getClassName('bullet-list')]}>
                {bulletPoints.map((point, index) => (
                  <li
                    key={index}
                    className={styles[getClassName('bullet-item')]}
                  >
                    <Icon
                      className={styles[getClassName('bullet-icon')]}
                      name='check'
                    />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={styles[getClassName('button-wrapper')]}>
            <BasicButton
              className={styles[getClassName('review-offer')]}
              data-testid={makeTestId('details')}
              name={formatMessage(
                {
                  defaultMessage:
                    '{hasTilaContent, select, true {Review offer summary {offerType}} other {Select {offerType}}}',
                  id: 'sputnik.EligibleOfferTile__svTpEp',
                },
                { offerType: decision?.offerType, hasTilaContent },
              )}
              type={isMostPopular ? BASIC_BUTTON_TYPE_FILLED : BASIC_BUTTON_TYPE_OUTLINED}
              onClick={handleViewOfferDetailsClick}
            >
              <FormattedMessage
                defaultMessage='{hasTilaContent, select, 
                true {Review offer summary}
                other {Select}}'
                id='sputnik.EligibleOfferTile__iJsPEe'
                values={{ hasTilaContent }}
              />
            </BasicButton>
            <EligibleOfferTileLearnMoreLink url={learnMoreLink} />
          </div>
        </div>
      </Tile>
      {!isMobile && tilaContent && decision?.offerType && (
        <AgreementModal
          hasAgreedToTila={hasAgreedToTila}
          isAcceptingTilaAgreement={isAcceptingTilaAgreement}
          isShowing={isShowingAgreementModal}
          tilaContent={tilaContent}
          onAgreeClick={handleAgreeToOfferSummary}
          onCancel={() => setIsShowingAgreementModal(false)}
        />
      )}
      {isMobile && tilaContent && decision?.offerType && (
        <AgreementPopsheet
          hasAgreedToTila={hasAgreedToTila}
          isAcceptingTilaAgreement={isAcceptingTilaAgreement}
          isShowing={isShowingAgreementModal}
          tilaContent={tilaContent}
          onAgreeClick={handleAgreeToOfferSummary}
          onCancel={() => setIsShowingAgreementModal(false)}
        />
      )}
    </>
  )
}

export default EligibleOfferTile
